import { useEffect, useState } from 'react'
import { fetchMe } from '../lib/api'
import type { User } from '../lib/types'

/**
 * Resolve the current user once on mount.
 *
 * Phase-1 mock auth: the backend always returns the seeded
 * ``u_default`` user when no ``X-User-ID`` header is sent, so this
 * never errors in the demo flow.  When real auth lands the same hook
 * keeps working — only the network identity will change.
 *
 * Returns:
 *   - ``user``    : the resolved user, or ``null`` while loading / on
 *                   error
 *   - ``loading`` : true until the first /me round-trip resolves
 *   - ``error``   : non-null only if the request failed (network down,
 *                   401, 5xx). The UI should fall back to a
 *                   "not signed in" state in that case.
 */
export function useCurrentUser(): {
  user: User | null
  loading: boolean
  error: string | null
} {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchMe()
      .then((u) => {
        if (!cancelled) {
          setUser(u)
          setError(null)
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e)
          setError(msg)
          setUser(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { user, loading, error }
}
